/**
 * Resolve ISO currency codes from job location / country and format salaries.
 * Keep aliases in sync with backend/src/utils/jobSalaryCurrency.js.
 */

const LOCATION_CURRENCY_RULES = [
  { re: /\b(united states of america|united states|u\.?\s*s\.?\s*a\.?|u\.?\s*s\.?|america)\b/i, code: 'USD' },
  { re: /\b(canada|canadian)\b/i, code: 'CAD' },
  { re: /\b(india|indian|bharat)\b/i, code: 'INR' },
  { re: /\b(united kingdom|u\.?\s*k\.?|great britain|england|scotland|wales)\b/i, code: 'GBP' },
  { re: /\b(australia|australian)\b/i, code: 'AUD' },
  { re: /\b(new zealand)\b/i, code: 'NZD' },
  { re: /\b(singapore)\b/i, code: 'SGD' },
  { re: /\b(hong kong)\b/i, code: 'HKD' },
  { re: /\b(united arab emirates|u\.?\s*a\.?\s*e\.?|dubai|abu dhabi)\b/i, code: 'AED' },
  { re: /\b(eurozone|european union)\b/i, code: 'EUR' },
  { re: /\b(germany|france|spain|italy|netherlands|ireland|belgium|austria|portugal|finland|greece)\b/i, code: 'EUR' },
  { re: /\b(japan|japanese)\b/i, code: 'JPY' },
  { re: /\b(china|chinese|prc)\b/i, code: 'CNY' },
  { re: /\b(switzerland|swiss)\b/i, code: 'CHF' },
  { re: /\b(mexico|mexican)\b/i, code: 'MXN' },
  { re: /\b(brazil|brazilian)\b/i, code: 'BRL' },
  { re: /\b(south africa)\b/i, code: 'ZAR' },
  { re: /\b(south korea|korea,?\s*republic)\b/i, code: 'KRW' },
];

/** Omit CA/IN — collide with US state codes; use "Canada"/"India" names instead. */
const ISO_COUNTRY_TO_CURRENCY = {
  US: 'USD',
  USA: 'USD',
  CAN: 'CAD',
  IND: 'INR',
  GB: 'GBP',
  UK: 'GBP',
  AU: 'AUD',
  NZ: 'NZD',
  SG: 'SGD',
  HK: 'HKD',
  AE: 'AED',
  JP: 'JPY',
  CN: 'CNY',
  CH: 'CHF',
  MX: 'MXN',
  BR: 'BRL',
  ZA: 'ZAR',
  KR: 'KRW',
  DE: 'EUR',
  FR: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  IE: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  PT: 'EUR',
  FI: 'EUR',
  GR: 'EUR',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  PL: 'PLN',
  EU: 'EUR',
};

const CA_PROVINCES = new Set(['ON', 'BC', 'AB', 'QC', 'MB', 'SK', 'NS', 'NB', 'NL', 'PE', 'YT', 'NT', 'NU']);

/**
 * @param {string|null|undefined} location
 * @param {string|null|undefined} [explicitCurrency]
 */
export function currencyCodeFromLocation(location, explicitCurrency = null) {
  if (explicitCurrency && /^[A-Za-z]{3}$/.test(String(explicitCurrency).trim())) {
    return String(explicitCurrency).trim().toUpperCase();
  }

  const text = String(location || '').trim();
  if (!text) return 'USD';

  for (const rule of LOCATION_CURRENCY_RULES) {
    if (rule.re.test(text)) return rule.code;
  }

  const parts = text.split(/[,|/·•]+/).map((p) => p.trim()).filter(Boolean);
  const norm = parts.map((p) => p.toUpperCase().replace(/\./g, ''));
  if (norm.length >= 2) {
    const last = norm[norm.length - 1];
    const prev = norm[norm.length - 2].split(/\s+/).pop();
    if (last === 'CA' && CA_PROVINCES.has(prev)) return 'CAD';
  }
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const token = norm[i];
    if (ISO_COUNTRY_TO_CURRENCY[token]) return ISO_COUNTRY_TO_CURRENCY[token];
    const words = token.split(/\s+/);
    const last = words[words.length - 1];
    if (ISO_COUNTRY_TO_CURRENCY[last]) return ISO_COUNTRY_TO_CURRENCY[last];
  }

  return 'USD';
}

export function currencyCodeForJob(job) {
  if (!job) return 'USD';
  return currencyCodeFromLocation(job.location, job.currency || job.salary_currency);
}

function fmtAmount(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  return num.toLocaleString();
}

/**
 * Format salary with country-based ISO code (USD / CAD / INR …), not `$`.
 */
export function formatJobSalary(job) {
  if (!job) return null;
  const hasMin = job.salary_min != null && job.salary_min !== '';
  const hasMax = job.salary_max != null && job.salary_max !== '';
  if (!hasMin && !hasMax) return null;

  const code = currencyCodeForJob(job);
  if (hasMin && hasMax) {
    return `${code} ${fmtAmount(job.salary_min)} – ${fmtAmount(job.salary_max)}`;
  }
  if (hasMin) return `${code} ${fmtAmount(job.salary_min)}+`;
  return `Up to ${code} ${fmtAmount(job.salary_max)}`;
}
